import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const RenderAnswerChoices = ({ answer_bank, exampleQuestion }) => (
  <div className="exercise-card">
    <h3 className="font-display font-bold text-xl text-slate-800 dark:text-white flex items-center gap-2 mb-6">
      <span className="material-symbols-outlined text-primary-blue">grid_view</span>
      Answer Choices
    </h3>
    <div className="space-y-3">
      {Object.entries(answer_bank).map(([letter, text]) => {
        const isExample = exampleQuestion && exampleQuestion.accepted_answers.includes(letter);
        return (
          <div key={letter} className={`flex items-start gap-3 p-2 rounded-lg ${isExample ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
            <span className="font-display font-bold text-primary-blue text-sm">{letter}</span>
            <p className="font-body text-sm text-gray-700 dark:text-gray-300 m-0">
              {text.replace(/\(Example\)/gi, '').trim()}
              {isExample && <span className="font-bold text-primary-blue text-[10px] ml-2 uppercase">(Example)</span>}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const DialogueLine = ({ character, text, question, isExample = false, answer, isHydrated, isReviewMode, showHint, handleAnswerChange, character_1, character_2 }) => {
  const isResponder = character.name === character_2.name;
  const isCorrect = isReviewMode && (question?.is_correct === true || question?.is_correct === 'true');
  const showIncorrectAnswer = isReviewMode && !isExample && !isCorrect && question;

  const avatarIcon = isResponder ? (character_2.avatar || 'face_3') : (character_1.avatar || 'face');

  return (
    <div className="flex items-center gap-2">
      {/* Compact avatar */}
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white shadow-sm ${isResponder ? 'bg-pink-400' : 'bg-blue-400'}`}>
        <span className="material-symbols-outlined text-base">{avatarIcon}</span>
      </div>

      {text ? (
        <div className="bg-slate-100 dark:bg-slate-700/50 px-3 py-1.5 rounded-xl rounded-tl-none shadow-sm flex-1">
          <p className="m-0 text-sm text-gray-800 dark:text-gray-200 leading-snug">{text}</p>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-1">
          <span className="font-bold text-slate-300 tracking-widest text-sm">.......</span>
          {showIncorrectAnswer ? (
            <div className="exercise-input-text inline-flex items-center bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 h-10 min-w-[2.5rem] justify-center mx-0.5">
              <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-70 text-sm mr-1">{question.user_answer?.answer || '(empty)'}</span>
              <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
            </div>
          ) : (
            <input
              type="text"
              maxLength="1"
              autoComplete="off"
              className={`exercise-input-field w-10 h-10 text-base !rounded-lg mx-0.5 shadow-sm border-2 ${isExample ? 'exercise-example-text border-blue-300 bg-blue-50' : 'exercise-input-text border-slate-200'} ${showHint && !isExample ? 'hint-input' : ''}`}
              value={isExample ? question.accepted_answers[0] : (showHint || isCorrect ? question.accepted_answers[0] : answer || '')}
              onChange={(e) => handleAnswerChange(question.id, e)}
              placeholder="?"
              disabled={isReviewMode || showHint || !isHydrated}
            />
          )}
          <span className="font-bold text-slate-300 tracking-widest text-sm">.......</span>
          {isReviewMode && (isCorrect
            ? <span className="correct-checkmark text-sm">✓</span>
            : <span className="incorrect-checkmark text-sm">✗</span>
          )}
        </div>
      )}
    </div>
  );
};

const RenderConversation = ({ exampleQuestion, mainQuestions, answers, isHydrated, isReviewMode, showHint, handleAnswerChange, character_1, character_2 }) => {
  const allItems = exampleQuestion ? [{ ...exampleQuestion, isExample: true }, ...mainQuestions] : mainQuestions;
  return (
    <div className="space-y-2">
      {allItems.map((q) => (
        <div
          key={q.id}
          className={`bg-white dark:bg-slate-800/50 rounded-2xl border-2 shadow-sm hover:border-gray-300 transition-colors px-3 py-2 relative flex items-center gap-3 ${q.isExample ? 'border-gray-200/60 dark:border-slate-600' : 'border-gray-200 dark:border-slate-600'
            }`}
        >
          {/* Number badge on the left */}
          <div className="shrink-0">
            {q.isExample
              ? <span className="font-black text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-lg text-xs border border-gray-200">e.g.</span>
              : <span className="font-black text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-lg text-xs border border-gray-200">{q.order_index}.</span>
            }
          </div>

          {/* Dialogue content stacked */}
          <div className="flex-1 flex flex-col gap-1.5 min-w-0">
            <DialogueLine character={character_1} text={q.question_text} character_1={character_1} character_2={character_2} />
            <DialogueLine
              character={character_2}
              question={q}
              answer={answers[q.id]}
              isExample={q.isExample || false}
              isHydrated={isHydrated}
              isReviewMode={isReviewMode}
              showHint={showHint}
              handleAnswerChange={handleAnswerChange}
              character_1={character_1}
              character_2={character_2}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ReadingPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);


  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    answer_bank,
    character_1,
    character_2,
  } = exerciseData.content_data;

  const exampleQuestion = exerciseData.questions.find(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index !== 0).sort((a, b) => a.order_index - b.order_index);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleAnswerChange = (questionId, event) => {
    const typedAnswer = event.target.value.toUpperCase();
    setAnswers(prev => ({ ...prev, [questionId]: typedAnswer }));
  };

  return (
    <div className="w-full h-full">
      <div className="flex flex-col h-full w-full">
        <div className="w-full m-auto">
          {image_url && (
            <div className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-soft border-2 border-white dark:border-card-dark overflow-hidden shrink-0 mb-4">
              <img
                src={`${ASSET_BASE_URL}${image_url}`}
                alt="Exercise Context"
                className="mx-auto rounded-xl w-auto max-w-full max-h-40 object-contain shadow-sm"
              />
            </div>
          )}
          <RenderConversation
            exampleQuestion={exampleQuestion}
            mainQuestions={mainQuestions}
            answers={answers}
            isHydrated={isHydrated}
            isReviewMode={isReviewMode}
            showHint={showHint}
            handleAnswerChange={handleAnswerChange}
            character_1={character_1}
            character_2={character_2}
          />
        </div>
      </div>
    </div>
  );
};


export default ReadingPart2;